// What a β report carries besides the tester's words: where they were, what they just did, what broke, and the state
// the screen was drawn from. Triage (AGENTS.md "Feedback") reproduces from this. Recent chat is optional and is never
// attached after a crisis card.
import type { Message } from '@shared/types';
import { appStore } from '../store';
import { routerStore, SCREEN_NAMES } from '../router';
import { currentLayout } from '../layout';
import { currentPushMode, platform } from '../native';
import { draftStore } from '../screens/onboarding/draft';
import { recentErrors, recentTrail } from '../lib/trail';
import { BUILD } from './build';

export const CHAT_MESSAGES = 10;

/** Recent chat would include a crisis moment (or the chat is showing one now): never attach it. */
export function chatHasCrisis(messages: Message[] = appStore.get().state?.messages || []): boolean {
  return appStore.get().chat.crisis || messages.slice(-40).some((m) => m.kind === 'crisis');
}

export function canAttachChat(): boolean {
  const st = appStore.get().state;
  return !!st && st.messages.length > 0 && !chatHasCrisis(st.messages);
}

export function collectContext(o: { includeChat: boolean; shell: { version: string; build?: string } | null }): Record<string, unknown> {
  const app = appStore.get();
  const r = routerStore.get();
  const st = app.state;
  const ctx: Record<string, unknown> = {
    at: new Date().toISOString(),
    client: platform(),
    shell: o.shell ? o.shell.version + (o.shell.build ? ` (${o.shell.build})` : '') : null,
    build: BUILD,
    screen: r.s,
    screenName: SCREEN_NAMES[r.s],
    params: Object.fromEntries(Object.entries(r.params).filter(([, v]) => v != null && typeof v !== 'object')),
    hist: r.hist.slice(-6).map((h) => h.s),
    layout: currentLayout(),
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100 },
    tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } })(),
    lang: navigator.language,
    ua: navigator.userAgent.slice(0, 300),
    online: navigator.onLine,
    live: app.live,
    push: currentPushMode(),
    signedIn: !!app.token,
    trail: recentTrail(),
    errors: recentErrors(),
  };
  if (st) {
    Object.assign(ctx, {
      uid: st.user.id, ch: st.ch, dayIndex: st.dayIndex, debugDays: st.user.debugDays, now: st.now, today: st.today, v: st.v,
      level: st.level.level, points: st.points, streak: st.streak.count, litToday: st.streak.litToday,
      settings: { planTime: st.user.planTime, checkin: st.user.checkin, reminders: st.user.reminders, vip: st.user.vip },
      plan: st.plan.status,
      cards: st.plan.cards.map((c) => ({ id: c.id, time: c.time, title: c.title, st: c.st, min: c.min, source: c.source, ...(c.startedAt ? { startedAt: c.startedAt } : {}), ...(c.doneAt ? { doneAt: c.doneAt } : {}) })),
      tomorrow: st.tomorrow.length,
      usage: { messagesToday: st.usage.messagesToday, freeLimit: st.usage.freeLimit },
      chatStatus: { sending: app.chat.sending, error: app.chat.error, limit: app.chat.limit },
      pendingReward: !!st.reward,
    });
    if (chatHasCrisis(st.messages)) { ctx.messages = null; ctx.messagesOmitted = 'crisis'; }
    else if (!o.includeChat) { ctx.messages = null; ctx.messagesOmitted = 'tester chose not to attach'; }
    else {
      ctx.messages = st.messages.slice(-CHAT_MESSAGES).map((m) => ({
        ts: m.ts, role: m.role, kind: m.kind, text: m.text.slice(0, 500), ...(m.voice ? { voice: true } : {}), ...(m.image ? { image: true } : {}),
        ...(m.kind === 'plan' || m.kind === 'change' ? { data: m.data } : {}),
      }));
    }
  } else {
    const d = draftStore.get();
    ctx.onboarding = { screen: d.screen ?? null, invite: d.invite || null, ch: d.ch ?? null, firstDone: !!d.first };
  }
  return ctx;
}
